<template>
  <div class="admin-wrapper">
    <SidebarGeneral
      :activeTab="activeTab"
      :userRole="authStore.role"
      :userName="displayName"
      :pageTitle="pageTitle"
      @update:activeTab="emit('update:activeTab', $event)"
      @toggle="handleToggle"
      @logout="handleLogout"
    />

    <main class="main-content" :style="{ marginLeft: contentMargin }">
      <slot />
    </main>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import SidebarGeneral from "@/components/Layout/SidebarGeneral.vue";
import { useAuthStore } from "@/store/useAuthStore";
import { NAV_CONFIG } from "@/config/navigation";
import { logoutAdmin } from "@/utils/api_backend";
import { getAuthStorage } from "@/utils/authStorage";

const props = defineProps({
  activeTab: { type: String, required: true },
});

// "toggle" se reemite hacia afuera para páginas que necesitan reaccionar
// al colapso del sidebar (p. ej. redibujar FullCalendar).
const emit = defineEmits(["update:activeTab", "toggle"]);

const router = useRouter();
const authStore = useAuthStore();
const contentMargin = ref("260px");

const displayName = computed(() =>
  `${authStore.userName} ${authStore.userApellido}`.trim()
);

// Título de la página activa, derivado de NAV_CONFIG (misma fuente que el
// menú) — lo usa el navbar móvil para mostrarlo centrado, ya que en móvil
// se quita del toolbar de cada Crud para ganar espacio vertical.
const pageTitle = computed(() => {
  for (const section of NAV_CONFIG.sections) {
    const item = section.items.find((i) => i.tab === props.activeTab);
    if (item) return item.mobileLabel || item.label;
  }
  return "";
});

function handleToggle(isCollapsed) {
  contentMargin.value = isCollapsed ? "80px" : "260px";
  emit("toggle", isCollapsed);
}

async function handleLogout() {
  // Se lee directo del storage (no de authStore.refreshToken) porque el
  // access token pudo haberse renovado en silencio desde el último login,
  // y con ROTATE_REFRESH_TOKENS el refresh token vigente ya no es el que
  // guardó setSession originalmente.
  const refreshToken = getAuthStorage().getItem("refresh_token");
  // IMPORTANTE: debe ejecutarse ANTES de authStore.logout(). La petición
  // necesita el access token todavía presente en storage para pasar el
  // IsAuthenticated del backend (si se limpia primero, sale sin header
  // de Authorization y el backend responde 401).
  await logoutAdmin(refreshToken);
  authStore.logout();
  router.push("/login");
}
</script>

<style scoped>
.admin-wrapper {
  background: linear-gradient(rgba(20, 30, 40, 0.92), rgba(20, 30, 40, 0.75)),
    url("@/assets/img/Divisan/img_2.jpg");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  min-height: 100vh;
}

.main-content {
  transition: margin-left 0.3s ease;
  padding: 2rem;
}

@media (max-width: 991.98px) {
  .main-content {
    margin-left: 0 !important;
    padding-top: 85px;
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
